/**
 * Colour per status value, in one place.
 *
 * Same rule as the sibling Hub platform: the invariant is NOT global hex
 * uniqueness - reuse across unrelated domains is fine, because those values
 * never appear side by side. What must hold is
 *   - no two values in the SAME picker share a tone, and
 *   - no value on a coloured control falls through to the grey fallback.
 * Both are pinned by `tests/statusColors.test.ts`.
 *
 * Keys are the raw database values, lower_snake, exactly as the CHECK
 * constraints store them. Labels are derived, never stored: renaming a value
 * here would silently stop a posting or workflow rule matching it.
 */
export type Tone = 'gray' | 'green' | 'blue' | 'red' | 'amber' | 'orange' | 'violet' | 'teal' | 'stone';

const STATUS_TONES: Record<string, Tone> = {
  // Not started. Grey means "nothing has happened yet", and only that.
  draft: 'gray',
  generated: 'gray',
  unassessed: 'gray',
  not_applicable: 'gray',
  unusable: 'gray',

  // Finished with, but not a failure: stone rather than grey, because a record
  // that WAS something and is now closed must not read as one that never began.
  inactive: 'stone',
  cancelled: 'stone',
  superseded: 'stone',
  expired: 'stone',

  // In flight
  pending: 'amber',
  // A representative the farmer has not yet confirmed by OTP.
  nominated: 'amber',
  submitted: 'amber',
  under_review: 'violet',
  returned: 'blue',
  renewal_due: 'amber',
  invited: 'blue',
  sent: 'blue',
  // Informational, not a warning: a plot awaiting its boundary sits next to
  // `suspended` in the same picker, and those are not the same kind of news.
  mapping_pending: 'blue',

  // Where a party stands on one consent (0126). These five sit together in one list, so they are
  // five distinguishable tones and not four plus a repeat.
  //   missing  - required by their role and NEVER ISSUED. Red: it is the finding, and it is the
  //              one state no list of consents could ever have shown, because it has no row.
  //   expiring - still valid, and the only moment anything can be done about it.
  //   valid    - green, and distinct from `verified` (teal) which is about the evidence.
  missing: 'red',
  expiring: 'orange',
  valid: 'green',

  // Settled, good
  active: 'green',
  approved: 'green',
  verified: 'teal',
  acknowledged: 'green',
  complete: 'green',
  activated: 'green',
  delivered: 'teal',

  // Settled, bad. Temporary and permanent are deliberately different colours:
  // a suspended record can come back, a terminated one is a compliance decision.
  // Suspended is orange, not amber, because the record-status picker puts it beside
  // `pending` (amber), and the two are not the same kind of news.
  suspended: 'orange',
  terminated: 'red',
  // The farmer chose to leave (0066): settled, but not a finding against them.
  withdrawn: 'stone',
  excluded: 'red',
  rejected: 'red',
  revoked: 'red',
  blocked: 'red',
  locked: 'red',
  failed: 'red',

  // Risk bands - their own picker, so tones may repeat from elsewhere but never
  // within the ladder. High and critical must be told apart at a glance.
  low: 'green',
  medium: 'amber',
  high: 'orange',
  critical: 'red',

  // Tree maturity
  immature: 'blue',
  semi_mature: 'teal',
  mature: 'green',
  over_mature: 'amber',

  // Asset condition
  good: 'green',
  fair: 'amber',
  poor: 'red',

  // Registers (0079 / 0080): pest records, accidents, complaints, consultations, audits, trades.
  open: 'amber',
  under_control: 'blue',
  resolved: 'green',
  investigating: 'violet',
  action_pending: 'orange',
  closed: 'stone',
  received: 'blue',
  escalated: 'red',
  planned: 'blue',
  held: 'green',
  minutes_shared: 'teal',
  in_progress: 'violet',
  completed: 'green',
  not_audited: 'gray',
  conforming: 'green',
  observation: 'blue',
  minor_nc: 'orange',
  major_nc: 'red',
  confirmed: 'green',
  unpaid: 'red',
  partly_paid: 'amber',
  paid: 'green',

  // Satellite harvest verdicts (0097): what was seen, never what it means.
  consistent: 'green',
  not_observed: 'gray',
  change_outside_window: 'red',
  inconclusive: 'amber',

  // Renewals due (0087) and work sites (0086)
  due_30: 'amber',
  due_90: 'blue',
  later: 'gray',
};

export const FALLBACK_TONE: Tone = 'gray';

export function toneFor(value?: string | null): Tone {
  if (!value) return FALLBACK_TONE;
  return STATUS_TONES[value.toLowerCase().replace(/\s+/g, '_')] ?? FALLBACK_TONE;
}

/** Every value this module knows a tone for. Used by the test that pins the pickers. */
export function knownStatusValues(): string[] {
  return Object.keys(STATUS_TONES);
}

/** `semi_mature` -> `Semi mature`. Display only; the raw value is what is stored. */
export function statusLabel(value?: string | null): string {
  if (!value) return '--';
  const text = value.replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}
